import axiosInstance from "./axiosConfig";
import { welcomeVideoForm } from "./outgoingIntakesApi";
import type {
  WelcomeVideo,
  WelcomeVideoUploadMeta
} from "./types/outgoingIntake";
import { handleAPIError } from "./utils/errors";

const BASE = "/welcome_videos";

const rethrow = (error: unknown): never => {
  const apiError = handleAPIError(error);
  throw apiError;
};

/** The welcome-video library: the attorney's own videos and the firm's. */
export const WelcomeVideosAPI = {
  async list(scope?: WelcomeVideo["scope"]): Promise<WelcomeVideo[]> {
    try {
      const response = await axiosInstance.get<{ videos: WelcomeVideo[] }>(
        BASE,
        { params: scope ? { scope } : undefined }
      );
      return Array.isArray(response.data?.videos) ? response.data.videos : [];
    } catch (error) {
      return rethrow(error);
    }
  },

  async upload(
    file: File | Blob,
    options: Omit<WelcomeVideoUploadMeta, "saveToMyVideos"> & {
      scope?: WelcomeVideo["scope"];
      title?: string | null;
      filename?: string;
      onProgress?: (fraction: number) => void;
      signal?: AbortSignal;
    } = {}
  ): Promise<WelcomeVideo> {
    try {
      const response = await axiosInstance.post<{ video: WelcomeVideo }>(
        BASE,
        welcomeVideoForm(
          file,
          options.filename ?? "welcome-video.webm",
          options
        ),
        {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: options.onProgress
            ? (e) => options.onProgress?.(e.total ? e.loaded / e.total : 0)
            : undefined,
          signal: options.signal
        }
      );
      return response.data.video;
    } catch (error) {
      return rethrow(error);
    }
  },

  async rename(videoId: string, title: string): Promise<WelcomeVideo> {
    try {
      const response = await axiosInstance.patch<{ video: WelcomeVideo }>(
        `${BASE}/${videoId}`,
        { title }
      );
      return response.data.video;
    } catch (error) {
      return rethrow(error);
    }
  },

  /** Refused (409, `video_in_use`) while a template still uses it. */
  async remove(videoId: string): Promise<void> {
    try {
      await axiosInstance.delete(`${BASE}/${videoId}`);
    } catch (error) {
      rethrow(error);
    }
  }
};
